import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatDateTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow, Pill, Skeleton, Tooltip } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import { agreementPdfHref, useReservation, type ReservationItem } from './api';
import { reservationStatusVariants, secondsUntil, unitStatusLabels, unitStatusVariants } from './labels';
import { OnSiteSignatureDialog } from './OnSiteSignatureDialog';
import { ReleaseReservationDialog } from './ReleaseReservationDialog';
import { ReserveUnitDialog } from './ReserveUnitDialog';
import { useReservationActions } from './useReservationActions';
import styles from './ReservationStatusCard.module.css';

export interface ReservationStatusCardProps {
  reservationId: string;
  /** Called after a change that the host page may want to reflect (undo, new reservation). */
  onChanged?: ((reservationId: string) => void) | undefined;
}

type DotState = 'done' | 'current' | 'open' | 'problem';

interface Row {
  key: string;
  title: string;
  meta: string;
  state: DotState;
}

function daysText(days: number): string {
  return days === 1 ? '1 Tag' : `${days} Tagen`;
}

/** The history of the API plus the steps that are still ahead (Reservierung 12: „Unterschrift", „Freigabe durch …"). */
function timelineRows(item: ReservationItem, approver: string): Row[] {
  const rows: Row[] = item.history.map((entry, index) => ({
    key: `${entry.at}-${index}`,
    title: entry.title,
    meta: [formatDateTime(entry.at), entry.byShort].filter(Boolean).join(' · '),
    state: /abgebrochen|Hängt fest/.test(entry.title) ? 'problem' : 'done',
  }));
  if (item.status === 'awaiting_signature') {
    rows.push({
      key: 'signature',
      title: 'Unterschrift',
      meta: [
        item.escalation.unsignedDays === 0
          ? 'seit heute offen'
          : `offen seit ${daysText(item.escalation.unsignedDays)}`,
        item.escalation.nextStep,
      ].join(' · '),
      state: 'current',
    });
    rows.push({ key: 'approval', title: `Freigabe durch ${approver}`, meta: '', state: 'open' });
  } else if (item.status === 'awaiting_approval') {
    rows.push({
      key: 'approval',
      title: `Freigabe durch ${approver}`,
      meta: item.signedAt ? `offen seit ${formatDate(item.signedAt)}` : 'offen',
      state: 'current',
    });
  }
  return rows;
}

/** Live „noch N s" of the inline undo button while the window is open. */
function useCountdown(until: string | null): number {
  const [seconds, setSeconds] = useState(() => (until ? secondsUntil(until) : 0));
  useEffect(() => {
    if (!until) {
      setSeconds(0);
      return;
    }
    setSeconds(secondsUntil(until));
    const timer = window.setInterval(() => setSeconds(secondsUntil(until)), 500);
    return () => window.clearInterval(timer);
  }, [until]);
  return seconds;
}

/**
 * Reservierung 12/13 for the unit record (Objekte 06) and the deal record's units tab: the state of a
 * reservation with its history, the escalation rule box while the signature is open, „Erneut erinnern" /
 * „Reservierung zurücknehmen" (with the undo toast), „Reservierung aufheben", and the withdrawn/released
 * summary with „Erneut reservieren".
 */
export function ReservationStatusCard({ reservationId, onChanged }: ReservationStatusCardProps) {
  const workspace = useWorkspace();
  const query = useReservation(reservationId);
  const actions = useReservationActions(reservationId);
  const [releasing, setReleasing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [reserving, setReserving] = useState(false);
  const item = query.data;
  const approver = workspace.approvalRecipientName ?? 'Admin';
  const undoUntil =
    item?.canUndo && item.undoUntil && secondsUntil(item.undoUntil) > 0 ? item.undoUntil : null;
  const undoSeconds = useCountdown(undoUntil);

  if (query.isPending) {
    return (
      <div className={styles.card} aria-busy="true">
        <Skeleton width="40%" height={18} />
        <div className={styles.skeletonRows}>
          <Skeleton width="55%" />
          <Skeleton width="70%" />
          <Skeleton width="45%" />
        </div>
      </div>
    );
  }
  if (query.isError || !item) {
    return (
      <Banner
        tone="danger"
        actions={
          <Button variant="secondary" size="small" onClick={() => void query.refetch()}>
            Erneut laden
          </Button>
        }
      >
        {query.error ? errorMessage(query.error) : 'Die Reservierung konnte nicht geladen werden.'}
      </Banner>
    );
  }

  const unit = item.unit;
  const closed = item.status === 'withdrawn' || item.status === 'released' || item.status === 'rejected';
  const callHref = `/contacts/${item.contact.id}?editor=call`;

  if (closed) {
    const summary =
      item.status === 'withdrawn' && item.withdrawn
        ? `Reservierung zurückgenommen${item.withdrawn.byShort ? ` von ${item.withdrawn.byShort}` : ''} · ${formatDateTime(item.withdrawn.at)}${item.withdrawn.reason ? ` · Grund: ${item.withdrawn.reason}` : ''}`
        : item.status === 'released' && item.released
          ? `Reservierung aufgehoben${item.released.byShort ? ` von ${item.released.byShort}` : ''} · ${formatDateTime(item.released.at)} · Grund: ${item.released.reasonLabel}${item.released.note ? ` · ${item.released.note}` : ''}`
          : item.approval
            ? `Reservierung abgelehnt${item.approval.byShort ? ` von ${item.approval.byShort}` : ''}${item.approval.at ? ` · ${formatDateTime(item.approval.at)}` : ''}${item.approval.rejectionReason ? ` · Grund: ${item.approval.rejectionReason}` : ''}${item.approval.rejectionNote ? ` · ${item.approval.rejectionNote}` : ''}`
            : item.statusLabel;
    const explanation =
      item.status === 'withdrawn'
        ? `Der Link an ${item.contact.name} ist ungültig. Der Deal bleibt in „${item.deal.stageName}“, die Selbstauskunft bleibt erhalten.`
        : item.status === 'released'
          ? `${unit.number} ist wieder frei und für alle reservierbar. ${item.contact.name} erhält eine Bestätigung per E-Mail; der Deal steht in „${item.deal.stageName}“.`
          : `${unit.number} ist wieder frei. ${item.contact.name} erhält keine automatische Nachricht — der Berater informiert den Kunden.`;
    return (
      <div className={styles.root}>
        <div className={styles.card}>
          <div className={styles.head}>
            <span className={styles.title}>{unit.number}</span>
            <Pill variant={unitStatusVariants[unit.status]}>{unitStatusLabels[unit.status]}</Pill>
            <span className={styles.headEnd}>
              {undoUntil ? (
                <Button
                  variant="secondary"
                  size="small"
                  loading={actions.pending === 'undo'}
                  onClick={() => void actions.undo().then((undone) => undone && onChanged?.(undone.id))}
                >
                  Rückgängig · noch {undoSeconds} s
                </Button>
              ) : null}
              {unit.status === 'available' ? (
                <Button variant="secondary" size="small" onClick={() => setReserving(true)}>
                  Erneut reservieren
                </Button>
              ) : null}
            </span>
          </div>
          <div className={styles.summary}>{summary}</div>
          <p className={styles.explanation}>{explanation}</p>
        </div>
        {actions.toast}
        <ReserveUnitDialog
          open={reserving}
          unitId={unit.id}
          contactId={item.contact.id}
          onClose={() => setReserving(false)}
          onReserved={(id) => {
            setReserving(false);
            onChanged?.(id);
          }}
        />
      </div>
    );
  }

  const rows = timelineRows(item, approver);
  const awaitingSignature = item.status === 'awaiting_signature';
  const unsigned = item.escalation.unsignedDays;
  const releaseTooltip = item.releaseBlockedHelper;
  const releaseButton = (
    <Button
      variant="secondary"
      disabled={!item.canRelease && !item.canRequestRelease}
      onClick={() => setReleasing(true)}
    >
      Reservierung aufheben
    </Button>
  );

  return (
    <div className={clsx(styles.root, awaitingSignature && styles.withRule)}>
      <div className={styles.main}>
        {awaitingSignature ? (
          <div className={styles.warning} role="status">
            <span className={styles.warningTitle}>
              {unsigned === 0
                ? 'Noch nicht unterschrieben'
                : `Seit ${daysText(unsigned)} nicht unterschrieben`}
            </span>
            <span className={styles.warningText}>{unit.number} ist so lange für alle anderen blockiert.</span>
          </div>
        ) : null}
        {item.aborted && awaitingSignature ? (
          <Banner tone="warning" title={`${item.contact.name} hat die Reservierung abgebrochen`}>
            {formatDateTime(item.aborted.at)}
            {item.aborted.reason ? ` · „${item.aborted.reason}“` : ''} · Die Einheit bleibt zurückgehalten,
            der Link funktioniert weiter.
          </Banner>
        ) : null}
        <div className={styles.card}>
          <div className={styles.head}>
            <span className={styles.title}>Reservierung {unit.number}</span>
            <Pill variant={reservationStatusVariants[item.status]}>{item.statusLabel}</Pill>
            {undoUntil ? (
              <span className={styles.headEnd}>
                <Button
                  variant="secondary"
                  size="small"
                  loading={actions.pending === 'undo'}
                  onClick={() => void actions.undo().then((undone) => undone && onChanged?.(undone.id))}
                >
                  Rückgängig · noch {undoSeconds} s
                </Button>
              </span>
            ) : null}
          </div>
          <ol className={styles.timeline} aria-label="Verlauf">
            {rows.map((row) => (
              <li key={row.key} className={styles.row}>
                <span className={clsx(styles.dot, styles[row.state])} aria-hidden="true" />
                <span className={styles.rowText}>
                  <span className={clsx(styles.rowTitle, row.state === 'open' && styles.rowTitleOpen)}>
                    {row.title}
                  </span>
                  {row.meta ? <span className={styles.rowMeta}>{row.meta}</span> : null}
                </span>
              </li>
            ))}
          </ol>
          <div className={styles.actions}>
            <Link to={callHref} className={styles.linkButton}>
              Anrufen
            </Link>
            {awaitingSignature ? (
              <>
                {item.canRemind ? (
                  <Button
                    variant="secondary"
                    loading={actions.pending === 'remind'}
                    onClick={() => void actions.remind()}
                  >
                    Erneut erinnern
                  </Button>
                ) : null}
                {item.canSign ? (
                  <Button variant="secondary" onClick={() => setSigning(true)}>
                    Vor Ort unterschreiben
                  </Button>
                ) : null}
                <span className={styles.spacer} />
                {item.canWithdraw ? (
                  <Button
                    variant="secondary"
                    loading={actions.pending === 'withdraw'}
                    onClick={() =>
                      void actions.withdraw().then((withdrawn) => withdrawn && onChanged?.(withdrawn.id))
                    }
                  >
                    Reservierung zurücknehmen
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <a
                  href={agreementPdfHref(item.id)}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.linkButton}
                >
                  Vereinbarung ansehen
                </a>
                {item.approval?.status === 'open' && item.approval.approvalId && item.canApprove ? (
                  <Link
                    to={`/tasks/approvals?approval=${item.approval.approvalId}`}
                    className={styles.linkButton}
                  >
                    Freigabe öffnen
                  </Link>
                ) : null}
                <span className={styles.spacer} />
                {releaseTooltip && !item.canRelease && !item.canRequestRelease ? (
                  <Tooltip content={releaseTooltip}>
                    <span className={styles.disabledAnchor} tabIndex={0}>
                      {releaseButton}
                    </span>
                  </Tooltip>
                ) : (
                  releaseButton
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {awaitingSignature ? (
        <aside className={styles.rule} aria-label="Regel">
          <Eyebrow as="div">Regel</Eyebrow>
          <dl className={styles.ruleRows}>
            <div className={styles.ruleRow}>
              <dt>Erinnerung an Kunden</dt>
              <dd>nach 3 Tagen</dd>
            </div>
            <div className={styles.ruleRow}>
              <dt>Task an Berater</dt>
              <dd>nach 5 Tagen</dd>
            </div>
            <div className={styles.ruleRow}>
              <dt>Hängt fest</dt>
              <dd>nach 10 Tagen</dd>
            </div>
            <div className={styles.ruleRow}>
              <dt>Frist</dt>
              <dd>keine</dd>
            </div>
          </dl>
          <p className={styles.ruleNote}>
            Die Einheit wird nie automatisch frei. Zurücknehmen kann der Berater jederzeit, {approver} auch
            fremde.
          </p>
        </aside>
      ) : null}
      {actions.toast}
      <ReleaseReservationDialog
        open={releasing}
        reservationId={item.id}
        onClose={() => setReleasing(false)}
        onReleased={(released) => {
          setReleasing(false);
          if (released) onChanged?.(released.id);
        }}
      />
      {item.canSign ? (
        <OnSiteSignatureDialog
          key={item.id}
          open={signing}
          reservation={item}
          onClose={() => setSigning(false)}
          onSigned={(signed) => {
            setSigning(false);
            onChanged?.(signed.id);
          }}
        />
      ) : null}
    </div>
  );
}
